import app from "app";
import { rgbToInt, stringToNormalizedRgbColor } from "libs/colors";
import { V3 } from "libs/mjs";
import Toast from "libs/toast";
import window from "libs/window";
import debounce from "lodash-es/debounce";
import {
  BoxGeometry,
  BufferGeometry,
  EdgesGeometry,
  Euler,
  Group,
  Line,
  LineBasicMaterial,
  LineSegments,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  type Object3D,
  type Ray,
  Scene,
  Vector3 as ThreeVector3,
  type WebGLRenderer,
} from "three";
import { acceleratedRaycast, computeBoundsTree, disposeBoundsTree } from "three-mesh-bvh";
import type { BoundingBoxMinMaxType } from "types/bounding_box";
import type { OrthoView, OrthoViewMap, OrthoViewWithoutTDMap, Vector3 } from "viewer/constants";
import constants, {
  OrthoViews,
  OrthoViewValuesWithoutTDView,
  TDViewDisplayModeEnum,
} from "viewer/constants";
import { destroyRenderer, getRenderer } from "viewer/controller/renderer";
import {
  setSceneController,
  waitForPendingSceneControllerTeardown,
} from "viewer/controller/scene_controller_provider";
import type FlightModePlane from "viewer/geometries/arbitrary_plane";
import computeSplitBoundaryMeshWithSplines from "viewer/geometries/compute_split_boundary_mesh_with_splines";
import Cube from "viewer/geometries/cube";
import {
  ContourGeometry,
  LineMeasurementGeometry,
  QuickSelectGeometry,
} from "viewer/geometries/helper_geometries";
import { MipVolume } from "viewer/geometries/mip_volume";
import Plane from "viewer/geometries/plane";
import Skeleton from "viewer/geometries/skeleton";
import { reuseInstanceOnEquality } from "viewer/model/accessors/accessor_helpers";
import {
  getDataLayers,
  getLayerBoundingBox,
  getLayerByName,
  getSegmentationLayers,
  getVisibleSegmentationLayers,
} from "viewer/model/accessors/dataset_accessor";
import {
  getTransformedDatasetBoundingBox,
  getTransformsForLayer,
  getTransformsForLayerOrNull,
  getTransformsForSkeletonLayer,
} from "viewer/model/accessors/dataset_layer_transformation_accessor";
import {
  getActiveMagIndicesForLayers,
  getPosition,
  getRotationInRadian,
} from "viewer/model/accessors/flycam_accessor";
import {
  getSkeletonTracing,
  isSkeletonLayerVisible,
  isSkeletonSectionClippingActive,
} from "viewer/model/accessors/skeletontracing_accessor";
import {
  getMipEnabledBBoxes,
  getSomeTracing,
  getTaskBoundingBoxes,
  type MipEnabledBBox,
} from "viewer/model/accessors/tracing_accessor";
import { getPlaneScalingFactor } from "viewer/model/accessors/view_mode_accessor";
import { sceneControllerInitializedAction } from "viewer/model/actions/actions";
import {
  loadMipAction,
  removeMipLayerForBBoxAction,
} from "viewer/model/actions/annotation_actions";
import Dimensions from "viewer/model/dimensions";
import { listenToStoreProperty } from "viewer/model/helpers/listener_helpers";
import type { Transform } from "viewer/model/helpers/transformation_helpers";
import { OrthoBaseRotations } from "viewer/ortho_base_rotations";
import { Model } from "viewer/singletons";
import type {
  MipLayerConfig,
  SkeletonTracing,
  UserBoundingBox,
  WebknossosState,
} from "viewer/store";
import Store from "viewer/store";
import type CustomLOD from "./custom_lod";
import SegmentMeshController from "./segment_mesh_controller";

// Add the extension functions
BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
Mesh.prototype.raycast = acceleratedRaycast;

const CUBE_COLOR = 0x999999;

const getVisibleSegmentationLayerNames = reuseInstanceOnEquality((storeState: WebknossosState) =>
  getVisibleSegmentationLayers(storeState).map((l) => l.name),
);

class SceneController {
  public skeletons: Record<number, Skeleton> = {};
  private isPlaneVisible: OrthoViewMap<boolean>;
  private clippingDistanceInUnit: number;
  private datasetBoundingBox!: Cube;
  private userBoundingBoxGroup!: Group;
  private layerBoundingBoxGroup!: Group;
  private userBoundingBoxes!: Array<Cube>;
  private layerBoundingBoxes!: { [layerName: string]: Cube };
  private annotationToolsGeometryGroup!: Group;
  private highlightedBBoxId: number | null | undefined;
  private taskCubeByTracingId: Record<string, Cube | null | undefined> = {};
  public contour!: ContourGeometry;
  public quickSelectGeometry!: QuickSelectGeometry;
  public lineMeasurementGeometry!: LineMeasurementGeometry;
  public areaMeasurementGeometry!: ContourGeometry;
  private planes!: OrthoViewWithoutTDMap<Plane>;
  private rootNode!: Group;
  public renderer!: WebGLRenderer;
  public scene!: Scene;
  public rootGroup!: Group;
  public segmentMeshController: SegmentMeshController;
  private storePropertyUnsubscribers: Array<() => void>;
  private splitBoundaryMesh: Mesh | null = null;
  private mipVolumes = new Map<
    number,
    { volume: MipVolume; configs: MipLayerConfig[]; bbox: UserBoundingBox }
  >();
  private debouncedUpdateMipVolumes = debounce(
    (entries: MipEnabledBBox[]) => this.updateMipVolumes(entries),
    300,
  );
  // In-flight renderer.compileAsync() calls, registered by PlaneView/ArbitraryView.
  // See waitForPendingCompiles() for why these need to be tracked.
  private pendingCompilePromises: Set<Promise<unknown>> = new Set();

  // Created as instance properties to avoid creating objects in each update call.
  private rotatedPositionOffsetVector = new ThreeVector3();
  private flycamRotationEuler = new Euler();

  // This class collects all the meshes displayed in the Skeleton View and updates position and scale of each
  // element depending on the provided flycam.
  constructor() {
    this.isPlaneVisible = {
      [OrthoViews.PLANE_XY]: true,
      [OrthoViews.PLANE_YZ]: true,
      [OrthoViews.PLANE_XZ]: true,
      [OrthoViews.TDView]: true,
    };
    this.clippingDistanceInUnit = 0;
    this.segmentMeshController = new SegmentMeshController();
    this.storePropertyUnsubscribers = [];
  }

  initialize() {
    this.renderer = getRenderer();
    this.createMeshes();
    this.bindToEvents();
    this.scene = new Scene();
    this.highlightedBBoxId = null;
    this.rootGroup = new Group();
    this.scene.add(
      this.rootGroup.add(
        this.rootNode,
        this.segmentMeshController.meshesLayerLODRootGroup,
        this.segmentMeshController.lightsGroup,
      ),
    );
    // Because the voxel coordinates do not have a cube shape but are distorted,
    // we need to distort the entire scene to provide an illustration that is
    // proportional to the actual size in nm.
    // For some reason, all objects have to be put into a group object. Changing
    // scene.scale does not have an effect.
    // The dimension(s) with the highest resolution will not be distorted.
    this.rootGroup.scale.copy(
      new ThreeVector3(...Store.getState().dataset.dataSource.scale.factor),
    );
    this.setupDebuggingMethods();
  }

  setupDebuggingMethods() {
    // These methods are attached to window, since we would run into circular import errors
    // otherwise.
    // @ts-expect-error
    window.addBucketMesh = (
      position: Vector3,
      zoomStep: number,
      mag: Vector3,
      optColor?: string | null | undefined,
    ) => {
      const bucketSize = [
        constants.BUCKET_WIDTH * mag[0],
        constants.BUCKET_WIDTH * mag[1],
        constants.BUCKET_WIDTH * mag[2],
      ];
      const boxGeometry = new BoxGeometry(...bucketSize);
      const edgesGeometry = new EdgesGeometry(boxGeometry);
      const material = new LineBasicMaterial({
        color: optColor || (zoomStep === 0 ? 0xff00ff : 0x00ffff),
        linewidth: 1,
      });
      const cube = new LineSegments(edgesGeometry, material);
      cube.position.x = position[0] + bucketSize[0] / 2;
      cube.position.y = position[1] + bucketSize[1] / 2;
      cube.position.z = position[2] + bucketSize[2] / 2;
      this.rootNode.add(cube);
      return cube;
    };

    // @ts-expect-error
    window.addVoxelMesh = (position: Vector3, _cubeLength: Vector3, optColor?: string) => {
      // Shrink voxels a bit so that it's easier to identify individual voxels.
      const cubeLength = _cubeLength.map((el) => el * 0.9);

      const boxGeometry = new BoxGeometry(...cubeLength);
      const material = new MeshBasicMaterial({
        color: optColor || 0xff00ff,
        opacity: 0.5,
      });
      const cube = new Mesh(boxGeometry, material);
      cube.position.x = position[0] + cubeLength[0] / 2;
      cube.position.y = position[1] + cubeLength[1] / 2;
      cube.position.z = position[2] + cubeLength[2] / 2;
      this.rootNode.add(cube);
      return cube;
    };

    let renderedLines: Line[] = [];

    // Utility function for visual debugging
    // @ts-expect-error
    window.addLine = (a: Vector3, b: Vector3) => {
      const material = new LineBasicMaterial({
        color: 0x0000ff,
      });
      const points = [];
      points.push(new ThreeVector3(...a));
      points.push(new ThreeVector3(...b));
      const geometry = new BufferGeometry().setFromPoints(points);
      const line = new Line(geometry, material);
      this.rootNode.add(line);
      renderedLines.push(line);
    };

    // Utility function for visual debugging
    // @ts-expect-error
    window.removeLines = () => {
      for (const line of renderedLines) {
        this.rootNode.remove(line);
      }

      renderedLines = [];
    };

    // @ts-expect-error
    window.removeBucketMesh = (mesh: LineSegments) => this.rootNode.remove(mesh);
  }

  createMeshes(): void {
    this.userBoundingBoxes = [];
    this.userBoundingBoxGroup = new Group();
    this.layerBoundingBoxGroup = new Group();
    this.annotationToolsGeometryGroup = new Group();
    const state = Store.getState();
    // Cubes
    const { min, max } = getTransformedDatasetBoundingBox(
      state.dataset,
      state.datasetConfiguration.nativelyRenderedLayerName,
    );
    this.datasetBoundingBox = new Cube({
      min,
      max,
      color: CUBE_COLOR,
      showCrossSections: true,
      isHighlighted: false,
    });

    this.contour = new ContourGeometry();
    this.quickSelectGeometry = new QuickSelectGeometry();
    this.lineMeasurementGeometry = new LineMeasurementGeometry();
    this.areaMeasurementGeometry = new ContourGeometry(true);

    this.planes = {
      [OrthoViews.PLANE_XY]: new Plane(OrthoViews.PLANE_XY),
      [OrthoViews.PLANE_YZ]: new Plane(OrthoViews.PLANE_YZ),
      [OrthoViews.PLANE_XZ]: new Plane(OrthoViews.PLANE_XZ),
    };
    this.planes[OrthoViews.PLANE_XY].setBaseRotation(OrthoBaseRotations[OrthoViews.PLANE_XY]);
    this.planes[OrthoViews.PLANE_YZ].setBaseRotation(OrthoBaseRotations[OrthoViews.PLANE_YZ]);
    this.planes[OrthoViews.PLANE_XZ].setBaseRotation(OrthoBaseRotations[OrthoViews.PLANE_XZ]);

    const planeGroup = new Group();
    for (const plane of Object.values(this.planes)) {
      planeGroup.add(...plane.getMeshes());
    }
    // Apply the inverse dataset scale factor to all planes to remove the scaling of the root group
    // to avoid shearing effects on rotated ortho viewport planes. For more info see plane.ts.
    planeGroup.scale.copy(
      new ThreeVector3(1, 1, 1).divide(
        new ThreeVector3(...Store.getState().dataset.dataSource.scale.factor),
      ),
    );

    this.rootNode = new Group().add(
      this.userBoundingBoxGroup,
      this.layerBoundingBoxGroup,
      this.annotationToolsGeometryGroup.add(
        ...this.contour.getMeshes(),
        this.quickSelectGeometry.getMeshGroup(),
        ...this.lineMeasurementGeometry.getMeshes(),
        ...this.areaMeasurementGeometry.getMeshes(),
      ),
      ...this.datasetBoundingBox.getMeshes(),
      planeGroup,
    );

    if (state.annotation.skeleton != null) {
      this.addSkeleton((_state) => getSkeletonTracing(_state.annotation), true);
    }
    // Hide all objects at first, they will be made visible later if needed
    this.stopPlaneMode();
  }

  addSplitBoundaryMesh(points: Vector3[]) {
    if (points.length === 0) {
      return () => {};
    }

    let splitBoundaryMesh: Mesh | null = null;
    let splines: Object3D[] = [];
    try {
      const objects = computeSplitBoundaryMeshWithSplines(points);
      splitBoundaryMesh = objects.splitBoundaryMesh;
      splines = objects.splines;
    } catch (exc) {
      console.error(exc);
      Toast.error("Could not compute surface");
      return () => {};
    }

    const surfaceGroup = new Group();
    if (splitBoundaryMesh != null) {
      surfaceGroup.add(splitBoundaryMesh);
    }
    for (const spline of splines) {
      surfaceGroup.add(spline);
    }

    this.rootGroup.add(surfaceGroup);
    this.splitBoundaryMesh = splitBoundaryMesh;

    return () => {
      this.rootGroup.remove(surfaceGroup);
      this.splitBoundaryMesh = null;
    };
  }

  getSplitBoundaryMesh() {
    return this.splitBoundaryMesh;
  }

  private updateMipVolumes(storeMipEntries: MipEnabledBBox[]): void {
    // storeMipEntries are the MIP entries that are stored in the redux store.
    // These are synced with the MIPs held by the SceneController in this function.
    const storeMipsById = new Map(storeMipEntries.map((e) => [e.bbox.id, e]));

    // Remove volumes whose bbox changed (need full recreation)
    for (const [bboxId, { volume, bbox: storedBbox }] of this.mipVolumes) {
      const storeMip = storeMipsById.get(bboxId);
      const bb = storedBbox.boundingBox;
      const bboxChanged =
        storeMip == null ||
        !V3.equals(storeMip.bbox.boundingBox.min, bb.min) ||
        !V3.equals(storeMip.bbox.boundingBox.max, bb.max);
      if (bboxChanged) {
        this.rootNode.remove(volume.mesh);
        this.deferUntilCompileReady(() => volume.dispose());
        this.mipVolumes.delete(bboxId);
      }
    }

    for (const { bbox, configs } of storeMipEntries) {
      const mag1Bbox = { min: bbox.boundingBox.min, max: bbox.boundingBox.max };

      if (!this.mipVolumes.has(bbox.id)) {
        // New bbox: create volume and add all layers
        const volume = new MipVolume(mag1Bbox);
        const { mipRaymarchingSteps, mipDepthWrite } = Store.getState().userConfiguration;
        volume.setNumSteps(mipRaymarchingSteps);
        volume.setDepthWrite(mipDepthWrite);
        this.rootNode.add(volume.mesh);
        this.mipVolumes.set(bbox.id, { volume, configs, bbox });
      }

      const sceneMip = this.mipVolumes.get(bbox.id)!;
      sceneMip.bbox = bbox; // keep bbox reference current (e.g. isVisible changes)
      const { volume } = sceneMip;
      const oldConfigs = sceneMip.configs;

      // Remove layers that are no longer in the new configs
      const newLayerNames = new Set(configs.map((c) => c.layerName));
      const oldLayerNames = new Set(oldConfigs.map((c) => c.layerName));
      const removedLayerNames = oldLayerNames.difference(newLayerNames);
      for (const layerName of removedLayerNames) {
        volume.removeLayer(layerName);
      }

      // Add layers that are new
      for (const config of configs) {
        if (volume.hasLayer(config.layerName)) continue;
        if (volume.addLayer(config)) {
          Store.dispatch(loadMipAction(bbox.id, bbox, config));
        } else {
          // addLayer already showed an error toast (e.g. unsupported element class).
          // Remove the entry so it doesn't stay stuck in isLoading forever.
          Store.dispatch(removeMipLayerForBBoxAction(bbox.id, config.layerName));
        }
      }

      sceneMip.configs = configs;
    }
    app.vent.emit("rerender");
  }

  getMipHitPosition(ray: Ray): ThreeVector3 | null {
    for (const { volume, bbox } of this.mipVolumes.values()) {
      if (!bbox.isVisible) continue;
      const pos = volume.findMaxIntensityPosition(ray);
      if (pos != null) return pos;
    }
    return null;
  }

  getMipVolumeEntry(
    bboxId: number,
  ): { volume: MipVolume; configs: MipLayerConfig[]; bbox: UserBoundingBox } | undefined {
    return this.mipVolumes.get(bboxId);
  }

  addSkeleton(
    skeletonTracingSelector: (arg0: WebknossosState) => SkeletonTracing | null,
    supportsPicking: boolean,
  ): number {
    const skeleton = new Skeleton(skeletonTracingSelector, supportsPicking, (dispose) =>
      this.deferUntilCompileReady(dispose),
    );
    const skeletonGroup = skeleton.getRootGroup();
    this.skeletons[skeletonGroup.id] = skeleton;
    this.rootNode.add(skeletonGroup);
    return skeletonGroup.id;
  }

  removeSkeleton(skeletonId: number) {
    const skeleton = this.skeletons[skeletonId];
    const skeletonGroup = skeleton.getRootGroup();
    skeleton.destroy();
    delete this.skeletons[skeletonId];
    this.rootNode.remove(skeletonGroup);
  }

  updateTaskBoundingBoxes(
    taskCubeByTracingId: Record<string, BoundingBoxMinMaxType | null | undefined>,
  ): void {
    /*
     Ensures that a green task bounding box is rendered in the scene for
     each layer.
     The update is implemented by simply removing the old geometry and
     adding a new one. Since this function is executed very rarely,
     this is not a performance problem.
     */

    // Clean up old entries
    for (const [tracingId, _boundingBox] of Object.entries(this.taskCubeByTracingId)) {
      let taskCube = this.taskCubeByTracingId[tracingId];
      if (taskCube != null) {
        taskCube.getMeshes().forEach((mesh) => {
          this.rootNode.remove(mesh);
        });

        this.deferUntilCompileReady(() => taskCube.destroy());
      }
      this.taskCubeByTracingId[tracingId] = null;
    }
    // Add new entries
    for (const [tracingId, boundingBox] of Object.entries(taskCubeByTracingId)) {
      let taskCube = this.taskCubeByTracingId[tracingId];
      if (boundingBox == null || Store.getState().task == null) {
        continue;
      }
      const { viewMode } = Store.getState().temporaryConfiguration;
      taskCube = new Cube({
        min: boundingBox.min,
        max: boundingBox.max,
        color: 0x00ff00,
        showCrossSections: true,
        isHighlighted: false,
      });
      taskCube.getMeshes().forEach((mesh) => {
        this.rootNode.add(mesh);
      });

      if (viewMode === constants.MODE_FLIGHT) {
        taskCube?.setVisibility(false);
      }

      this.taskCubeByTracingId[tracingId] = taskCube;
    }
  }

  forEachTaskCube(fn: (cube: Cube) => void) {
    for (const cube of Object.values(this.taskCubeByTracingId)) {
      if (cube != null) {
        fn(cube);
      }
    }
  }

  updateSceneForCam = (id: OrthoView, hidePlanes: boolean = false): void => {
    // This method is called for each of the four cams. Even
    // though they are all looking at the same scene, some
    // things have to be changed for each cam.
    const state = Store.getState();
    const { dataset, userConfiguration, flycam } = state;
    const { tdViewDisplayPlanes, tdViewDisplayDatasetBorders } = userConfiguration;

    // Section clipping renders only the skeleton on the currently visible section.
    // It is performed in the node/edge shaders (in voxel/section space) and is only
    // possible when neither the camera nor the skeleton layer is rotated/transformed.
    // Otherwise we fall back to the regular distance-based clipping.
    const isSectionClippingActive = isSkeletonSectionClippingActive(state);
    // Only set the visibility of the dataset bounding box for the TDView.
    // This has to happen before updateForCam is called as otherwise cross section visibility
    // might be changed unintentionally.
    this.datasetBoundingBox.setVisibility(id !== OrthoViews.TDView || tdViewDisplayDatasetBorders);
    this.datasetBoundingBox.updateForCam(id);
    this.userBoundingBoxes.forEach((bbCube) => {
      bbCube.updateForCam(id);
    });
    // Layer bounding box visibility is kept in sync via a store subscription (see
    // updateLayerBoundingBoxVisibilities), so here we only refine the per-cam cross-section
    // visibility, like for user bounding boxes.
    Object.values(this.layerBoundingBoxes).forEach((bbCube) => {
      bbCube.updateForCam(id);
    });

    this.forEachTaskCube((cube) => cube.updateForCam(id));

    this.segmentMeshController.meshesLayerLODRootGroup.visible = id === OrthoViews.TDView;
    if (this.splitBoundaryMesh != null) {
      this.splitBoundaryMesh.visible = id === OrthoViews.TDView;
    }
    for (const { volume, bbox } of this.mipVolumes.values()) {
      volume.mesh.visible = id === OrthoViews.TDView && bbox.isVisible;
    }
    this.annotationToolsGeometryGroup.visible = id !== OrthoViews.TDView;
    this.lineMeasurementGeometry.updateForCam(id);

    const originalPosition = getPosition(flycam);
    const rotation = getRotationInRadian(flycam);

    // Tell the skeleton shaders which section to clip to for this render pass. The
    // 3D viewport (and any unsafe state) disables section clipping via clippingAxis -1.
    const clippingAxis =
      isSectionClippingActive && id !== OrthoViews.TDView ? Dimensions.getIndices(id)[2] : -1;
    for (const skeleton of Object.values(this.skeletons)) {
      skeleton.setSectionClippingUniforms(clippingAxis, originalPosition);
    }

    if (id !== OrthoViews.TDView) {
      for (const planeId of OrthoViewValuesWithoutTDView) {
        if (planeId === id) {
          this.planes[planeId].setOriginalCrosshairColor();
          this.planes[planeId].setVisible(!hidePlanes);
          this.flycamRotationEuler.set(...rotation, "ZYX");

          const ind = Dimensions.getIndices(planeId);
          // Offset the plane so the user can see the skeletonTracing behind the plane.
          // The offset is passed to the shader as a uniform to be subtracted from the position to render the correct data.
          // When section clipping is active, ensure the offset is at least one full
          // perpendicular voxel so that the (precise) shader-side clipping is never
          // overruled by the data plane occluding on-section nodes/edges.
          const effectiveClippingDistanceInUnit = isSectionClippingActive
            ? Math.max(this.clippingDistanceInUnit, dataset.dataSource.scale.factor[ind[2]])
            : this.clippingDistanceInUnit;
          const unrotatedPositionOffset = [0, 0, 0] as Vector3;
          unrotatedPositionOffset[ind[2]] =
            planeId === OrthoViews.PLANE_XY
              ? Math.floor(effectiveClippingDistanceInUnit)
              : Math.floor(-effectiveClippingDistanceInUnit);
          this.rotatedPositionOffsetVector
            .set(...unrotatedPositionOffset)
            .applyEuler(this.flycamRotationEuler);
          const rotatedPositionOffset = this.rotatedPositionOffsetVector.toArray();
          this.planes[planeId].setPosition(originalPosition, rotatedPositionOffset);
          this.planes[planeId].updateToFlycamRotation(this.flycamRotationEuler);

          this.quickSelectGeometry.adaptVisibilityForRendering(originalPosition, ind[2]);
        } else {
          this.planes[planeId].setVisible(false);
        }
        this.planes[planeId].materialFactory.uniforms.is3DViewBeingRendered.value = false;
      }
    } else {
      for (const planeId of OrthoViewValuesWithoutTDView) {
        this.planes[planeId].setPosition(originalPosition);
        this.planes[planeId].setGrayCrosshairColor();
        this.planes[planeId].setVisible(
          tdViewDisplayPlanes !== TDViewDisplayModeEnum.NONE,
          this.isPlaneVisible[planeId] && tdViewDisplayPlanes === TDViewDisplayModeEnum.DATA,
        );
        this.planes[planeId].materialFactory.uniforms.is3DViewBeingRendered.value = true;
      }
    }
  };

  update(optFlightModePlane?: FlightModePlane): void {
    const state = Store.getState();
    const { flycam } = state;
    const globalPosition = getPosition(flycam);

    const magIndices = getActiveMagIndicesForLayers(state);
    for (const dataLayer of Model.getAllLayers()) {
      dataLayer.layerRenderingManager.updateDataTextures(
        globalPosition,
        magIndices[dataLayer.name],
      );
    }

    if (!optFlightModePlane) {
      for (const currentPlane of Object.values(this.planes)) {
        const [scaleX, scaleY] = getPlaneScalingFactor(state, flycam, currentPlane.planeID);
        const isVisible = scaleX > 0 && scaleY > 0;

        if (isVisible) {
          this.isPlaneVisible[currentPlane.planeID] = true;
          currentPlane.setScale(scaleX, scaleY);
        } else {
          this.isPlaneVisible[currentPlane.planeID] = false;
          // Set the scale to non-zero values, since threejs will otherwise
          // complain about non-invertible matrices.
          currentPlane.setScale(1, 1);
        }
      }
    }
  }

  setDisplayCrosshair(value: boolean): void {
    for (const plane of Object.values(this.planes)) {
      plane.setDisplayCrosshair(value);
    }

    app.vent.emit("rerender");
  }

  setClippingDistance(valueInUnit: number): void {
    this.clippingDistanceInUnit = valueInUnit;
    app.vent.emit("rerender");
  }

  setInterpolation(value: boolean): void {
    for (const plane of Object.values(this.planes)) {
      plane.setLinearInterpolationEnabled(value);
    }

    app.vent.emit("rerender");
  }

  getRootNode(): Object3D {
    return this.rootNode;
  }

  setUserBoundingBoxes(bboxes: Array<UserBoundingBox>): void {
    for (const cube of this.userBoundingBoxes) {
      this.deferUntilCompileReady(() => cube.destroy());
    }
    const newUserBoundingBoxGroup = new Group();
    this.userBoundingBoxes = bboxes.map(({ boundingBox, isVisible, color, id }) => {
      const { min, max } = boundingBox;
      const bbColor: Vector3 = [color[0] * 255, color[1] * 255, color[2] * 255];
      const bbCube = new Cube({
        min,
        max,
        color: rgbToInt(bbColor),
        showCrossSections: true,
        id,
        isHighlighted: this.highlightedBBoxId === id,
      });
      bbCube.setVisibility(isVisible);
      bbCube.getMeshes().forEach((mesh) => {
        newUserBoundingBoxGroup.add(mesh);
      });
      return bbCube;
    });
    this.rootNode.remove(this.userBoundingBoxGroup);
    this.userBoundingBoxGroup = newUserBoundingBoxGroup;
    this.rootNode.add(this.userBoundingBoxGroup);
  }

  private applyTransformToGroup(transform: Transform, group: Group | CustomLOD) {
    if (transform.affineMatrix) {
      const matrix = new Matrix4();
      matrix.set(...transform.affineMatrix);
      // We need to disable matrixAutoUpdate as otherwise the update to the matrix will be lost.
      group.matrixAutoUpdate = false;
      group.matrix = matrix;
    }
  }

  updateGeometriesToTransforms(): void {
    /*
     * The following geometries are updated in accordance to the current transforms:
     * - user bounding boxes
     * - meshes
     * - annotation specific geometries (e.g., the contour)
     */
    const state = Store.getState();
    const tracingStoringUserBBoxes = getSomeTracing(state.annotation);
    const transformForBBoxes =
      tracingStoringUserBBoxes.type === "volume"
        ? getTransformsForLayer(
            state.dataset,
            getLayerByName(state.dataset, tracingStoringUserBBoxes.tracingId),
            state.datasetConfiguration.nativelyRenderedLayerName,
          )
        : getTransformsForSkeletonLayer(
            state.dataset,
            state.datasetConfiguration.nativelyRenderedLayerName,
          );
    this.applyTransformToGroup(transformForBBoxes, this.userBoundingBoxGroup);
    const visibleSegmentationLayers = getVisibleSegmentationLayers(state);
    if (visibleSegmentationLayers.length === 0) {
      return;
    }
    // Use transforms of active segmentation layer to transform the meshes.
    // All meshes not belonging to this layer should be hidden via updateMeshesAccordingToLayerVisibility anyway.
    const transformForMeshes = getTransformsForLayer(
      state.dataset,
      visibleSegmentationLayers[0],
      state.datasetConfiguration.nativelyRenderedLayerName,
    );
    this.applyTransformToGroup(
      transformForMeshes,
      this.segmentMeshController.meshesLayerLODRootGroup,
    );

    this.applyTransformToGroup(transformForMeshes, this.annotationToolsGeometryGroup);
  }

  updateMeshesAccordingToLayerVisibility(): void {
    const state = Store.getState();
    const visibleSegmentationLayers = getVisibleSegmentationLayers(state);
    const allSegmentationLayers = getSegmentationLayers(state.dataset);
    allSegmentationLayers.forEach((layer) => {
      const layerName = layer.name;
      const isLayerVisible =
        visibleSegmentationLayers.find((layer) => layer.name === layerName) !== undefined;
      this.segmentMeshController.setVisibilityOfMeshesOfLayer(layerName, isLayerVisible);
    });
  }

  updateLayerBoundingBoxes(): void {
    const state = Store.getState();
    const dataset = state.dataset;
    const layers = getDataLayers(dataset);
    const { layerBoundingBoxVisibilities, layerBoundingBoxColors } = state.temporaryConfiguration;

    // Destroy the old cubes to free their geometries/materials (see setUserBoundingBoxes).
    if (this.layerBoundingBoxes != null) {
      for (const cube of Object.values(this.layerBoundingBoxes)) {
        this.deferUntilCompileReady(() => cube.destroy());
      }
    }
    const newLayerBoundingBoxGroup = new Group();
    this.layerBoundingBoxes = Object.fromEntries(
      layers.map((layer) => {
        const boundingBox = getLayerBoundingBox(dataset, layer.name);
        const { min, max } = boundingBox;
        const color = layerBoundingBoxColors[layer.name] ?? stringToNormalizedRgbColor(layer.name);
        const bbCube = new Cube({
          min,
          max,
          color: rgbToInt([color[0] * 255, color[1] * 255, color[2] * 255]),
          // Show the bounding box in the 2D plane viewports as well (not just the 3D viewport), so it
          // is visible where users usually work and the visibility/color controls have a visible effect.
          showCrossSections: true,
          isHighlighted: false,
        });
        bbCube.setVisibility(layerBoundingBoxVisibilities[layer.name] ?? false);
        bbCube.getMeshes().forEach((mesh) => {
          const transformMatrix = getTransformsForLayerOrNull(
            dataset,
            layer,
            state.datasetConfiguration.nativelyRenderedLayerName,
          )?.affineMatrix;
          if (transformMatrix) {
            const matrix = new Matrix4();
            matrix.set(...transformMatrix);
            mesh.applyMatrix4(matrix);
          }
          newLayerBoundingBoxGroup.add(mesh);
        });
        return [layer.name, bbCube];
      }),
    );
    this.rootNode.remove(this.layerBoundingBoxGroup);
    this.layerBoundingBoxGroup = newLayerBoundingBoxGroup;
    this.rootNode.add(this.layerBoundingBoxGroup);
  }

  updateDatasetBoundingBoxToTransforms(): void {
    const state = Store.getState();
    const { min, max } = getTransformedDatasetBoundingBox(
      state.dataset,
      state.datasetConfiguration.nativelyRenderedLayerName,
    );
    this.datasetBoundingBox.setCorners(min, max);
  }

  // Visibility is a cheap per-cube flag (unlike the color, which is baked in at construction), so a
  // visibility change only updates the existing cubes instead of rebuilding them. updateSceneForCam
  // then refines the per-cam cross-section visibility on the next render.
  updateLayerBoundingBoxVisibilities(visibilities: Record<string, boolean>): void {
    for (const [layerName, bbCube] of Object.entries(this.layerBoundingBoxes)) {
      bbCube.setVisibility(visibilities[layerName] ?? false);
    }
    app.vent.emit("rerender");
  }

  highlightUserBoundingBox(bboxId: number | null | undefined): void {
    if (this.highlightedBBoxId === bboxId) {
      return;
    }

    const setIsHighlighted = (id: number, isHighlighted: boolean) => {
      const bboxToChangeHighlighting = this.userBoundingBoxes.find((bbCube) => bbCube.id === id);

      if (bboxToChangeHighlighting != null) {
        bboxToChangeHighlighting.setIsHighlighted(isHighlighted);
      }
    };

    if (this.highlightedBBoxId != null) {
      setIsHighlighted(this.highlightedBBoxId, false);
    }

    if (bboxId != null) {
      setIsHighlighted(bboxId, true);
    }

    this.highlightedBBoxId = bboxId;
  }

  setSkeletonGroupVisibility(isVisible: boolean) {
    Object.values(this.skeletons).forEach((skeleton: Skeleton) => {
      skeleton.getRootGroup().visible = isVisible;
    });
  }

  stopPlaneMode(): void {
    for (const plane of Object.values(this.planes)) {
      plane.setVisible(false);
    }

    this.datasetBoundingBox.setVisibility(false);
    this.userBoundingBoxGroup.visible = false;
    this.forEachTaskCube((cube) => cube.setVisibility(false));

    if (this.segmentMeshController.meshesLayerLODRootGroup != null) {
      this.segmentMeshController.meshesLayerLODRootGroup.visible = false;
    }
  }

  startPlaneMode(): void {
    for (const plane of Object.values(this.planes)) {
      plane.setVisible(true);
    }

    this.datasetBoundingBox.setVisibility(true);
    this.userBoundingBoxGroup.visible = true;

    this.forEachTaskCube((cube) => cube.setVisibility(true));
  }

  // Registers a pending renderer.compileAsync() promise; see PlaneView/ArbitraryView.start().
  registerPendingCompile(promise: Promise<unknown>): void {
    this.pendingCompilePromises.add(promise);
    promise.finally(() => {
      this.pendingCompilePromises.delete(promise);
    });
  }

  // three.js's compileAsync() polls internally via an uncancellable setTimeout loop until all
  // materials report their shader program is ready. If a tracked material (or the renderer) is
  // disposed while that poll is still in flight, the next tick crashes ("Cannot read properties
  // of undefined (reading 'isReady')"), since three.js's internal bookkeeping for that material
  // is gone. So any GPU-resource disposal that could race a pending compile must wait for it
  // first (see deferUntilCompileReady()). The timeout below is a safety net in case the GPU
  // context is already lost and the poll would otherwise never resolve.
  private async waitForPendingCompiles(timeoutMs = 5000): Promise<void> {
    if (this.pendingCompilePromises.size === 0) {
      return;
    }
    await Promise.race([
      Promise.allSettled(Array.from(this.pendingCompilePromises)),
      new Promise((resolve) => setTimeout(resolve, timeoutMs)),
    ]);
  }

  // Guards GPU-resource disposal against the compileAsync race described in
  // waitForPendingCompiles(). Needed at the point of disposal rather than sequenced once in
  // destroy(), since e.g. bounding box cubes get destroyed-and-recreated during normal
  // operation too (not just teardown).
  deferUntilCompileReady(dispose: () => void): void {
    if (this.pendingCompilePromises.size === 0) {
      dispose();
      return;
    }
    this.waitForPendingCompiles().then(dispose);
  }

  async destroy(): Promise<void> {
    // Stop listening to the store synchronously, before the await below. The caller resets the
    // store right after calling destroy(). Listeners that are still active would then rebuild
    // the scene objects from the empty default state.
    for (const fn of this.storePropertyUnsubscribers) {
      fn();
    }
    this.storePropertyUnsubscribers = [];
    this.debouncedUpdateMipVolumes.cancel();

    // See waitForPendingCompiles(); must happen before any disposal below, including
    // skeleton removal.
    await this.waitForPendingCompiles();

    // @ts-expect-error
    window.addBucketMesh = undefined;
    // @ts-expect-error
    window.addVoxelMesh = undefined;
    // @ts-expect-error
    window.addLine = undefined;
    // @ts-expect-error
    window.removeLines = undefined;
    // @ts-expect-error
    window.removeBucketMesh = undefined;

    for (const skeletonId of Object.keys(this.skeletons)) {
      this.removeSkeleton(Number.parseInt(skeletonId, 10));
    }

    for (const { volume } of this.mipVolumes.values()) {
      this.rootNode.remove(volume.mesh);
      volume.dispose();
    }
    this.mipVolumes.clear();

    this.segmentMeshController.destroy();

    this.contour.destroy();
    this.quickSelectGeometry.destroy();
    this.lineMeasurementGeometry.destroy();
    this.areaMeasurementGeometry.destroy();

    destroyRenderer();
    // @ts-expect-error
    this.renderer = null;

    this.datasetBoundingBox.destroy();
    this.userBoundingBoxes.forEach((cube) => {
      cube.destroy();
    });
    Object.values(this.layerBoundingBoxes).forEach((cube) => {
      cube.destroy();
    });
    this.forEachTaskCube((cube) => {
      cube.destroy();
    });

    for (const plane of Object.values(this.planes)) {
      plane.destroy();
    }

    this.rootNode = new Group();
  }

  bindToEvents(): void {
    this.storePropertyUnsubscribers = [
      listenToStoreProperty(
        (storeState) => storeState.userConfiguration.clippingDistance,
        (clippingDistance) => this.setClippingDistance(clippingDistance),
      ),
      listenToStoreProperty(
        (storeState) => storeState.userConfiguration.displayCrosshair,
        (displayCrosshair) => this.setDisplayCrosshair(displayCrosshair),
      ),
      listenToStoreProperty(
        (storeState) => storeState.datasetConfiguration.interpolation,
        (interpolation) => this.setInterpolation(interpolation),
      ),
      listenToStoreProperty(
        (storeState) => getSomeTracing(storeState.annotation).userBoundingBoxes,
        (bboxes) => this.setUserBoundingBoxes(bboxes),
      ),
      listenToStoreProperty(
        (storeState) => getDataLayers(storeState.dataset),
        () => {
          this.updateLayerBoundingBoxes();
          this.updateDatasetBoundingBoxToTransforms();
        },
      ),
      // The color is baked into the cubes at construction, so a color change requires rebuilding
      // them, whereas a visibility change (below) only needs to flip a per-cube flag.
      listenToStoreProperty(
        (storeState) => storeState.temporaryConfiguration.layerBoundingBoxColors,
        () => this.updateLayerBoundingBoxes(),
      ),
      listenToStoreProperty(
        (storeState) => storeState.temporaryConfiguration.layerBoundingBoxVisibilities,
        (visibilities) => this.updateLayerBoundingBoxVisibilities(visibilities),
      ),
      listenToStoreProperty(
        (storeState) => storeState.datasetConfiguration.nativelyRenderedLayerName,
        () => {
          this.updateLayerBoundingBoxes();
          this.updateGeometriesToTransforms();
          this.updateDatasetBoundingBoxToTransforms();
        },
      ),
      listenToStoreProperty(getVisibleSegmentationLayerNames, () =>
        this.updateMeshesAccordingToLayerVisibility(),
      ),
      listenToStoreProperty(
        (storeState) => getTaskBoundingBoxes(storeState),
        (boundingBoxesByTracingId) => this.updateTaskBoundingBoxes(boundingBoxesByTracingId),
        true,
      ),
      listenToStoreProperty(
        (storeState) => isSkeletonLayerVisible(storeState),
        (showSkeletons) => this.setSkeletonGroupVisibility(showSkeletons),
        true,
      ),
      listenToStoreProperty(getMipEnabledBBoxes, this.debouncedUpdateMipVolumes, true),
      listenToStoreProperty(
        (state) => state.userConfiguration.mipRaymarchingSteps,
        (numSteps) => {
          for (const { volume } of this.mipVolumes.values()) {
            volume.setNumSteps(numSteps);
          }
        },
        true,
      ),
      listenToStoreProperty(
        (state) => state.userConfiguration.mipDepthWrite,
        (enabled) => {
          for (const { volume } of this.mipVolumes.values()) {
            volume.setDepthWrite(enabled);
          }
        },
        true,
      ),
    ];
  }
}

export type SceneControllerType = SceneController;
export async function initializeSceneController(): Promise<void> {
  // Wait for the previous SceneController's teardown to finish first, since both share the
  // same WebGLRenderer singleton (see SceneController.destroy()).
  await waitForPendingSceneControllerTeardown();
  const controller = new SceneController();
  setSceneController(controller);
  controller.initialize();
  Store.dispatch(sceneControllerInitializedAction());
}

// Please use scene_controller_provider to get a reference to SceneController. This avoids
// problems with circular dependencies.
